// Pure tree-building for the people org chart. The API returns a flat list of
// active people; this assembles the reporting hierarchy from `managerId`.

export interface OrgChartPerson {
  id: string;
  name: string;
  title: string | null;
  managerId: string | null;
  avatarUrl: string | null;
}

export interface OrgChartNode {
  person: OrgChartPerson;
  children: OrgChartNode[];
}

/**
 * Build the reporting forest. Roots are people with no manager, a manager who
 * isn't in the list (e.g. inactive), or whose manager chain loops back to
 * them — a cycle guard so bad data degrades to extra roots instead of an
 * infinite loop. Children keep the input's alphabetical order.
 */
export function buildOrgForest(people: OrgChartPerson[]): OrgChartNode[] {
  const byId = new Map(people.map((p) => [p.id, p]));

  // A person is a root when they have no (known) manager, or they're a member
  // of a manager cycle (attaching them would loop the tree). People *below* a
  // cycle still attach to their manager normally — the cycle members
  // themselves surface as roots, so every node renders exactly once.
  const inCycle = (p: OrgChartPerson): boolean => {
    const seen = new Set([p.id]);
    let cur = p.managerId !== null ? byId.get(p.managerId) : undefined;
    while (cur) {
      if (cur.id === p.id) return true;
      if (seen.has(cur.id)) return false; // upstream cycle that doesn't include p
      seen.add(cur.id);
      cur = cur.managerId !== null ? byId.get(cur.managerId) : undefined;
    }
    return false;
  };
  const isRoot = (p: OrgChartPerson): boolean =>
    p.managerId === null || !byId.has(p.managerId) || inCycle(p);

  const nodes = new Map<string, OrgChartNode>(
    people.map((p) => [p.id, { person: p, children: [] }]),
  );
  const roots: OrgChartNode[] = [];
  for (const p of people) {
    const node = nodes.get(p.id);
    if (!node) continue;
    const parent = !isRoot(p) && p.managerId !== null ? nodes.get(p.managerId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

/**
 * Ids to expand so `personId` and their chain of command are visible on
 * first load: every ancestor up the manager chain, plus the person themself
 * (so their direct reports show). Empty when the person isn't in the list.
 */
export function chainOfCommandIds(people: OrgChartPerson[], personId: string): Set<string> {
  const byId = new Map(people.map((p) => [p.id, p]));
  const expanded = new Set<string>();
  let cur = byId.get(personId);
  while (cur && !expanded.has(cur.id)) {
    expanded.add(cur.id);
    cur = cur.managerId !== null ? byId.get(cur.managerId) : undefined;
  }
  return expanded;
}
